/**
 * Modal to allow updating the status of multiple stage runs at once.
 */
import {noop} from '@benchling/js-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import CheckableSortableTable from 'ui-library/CheckableSortableTable';
import LegacySelectDropdown from 'ui-library/dropdowns/LegacySelectDropdown';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import {STAGE_RUN_STATUS, STATUS_FRIENDLY_NAME} from 'workflows/constants/StageRunConstants';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';
import {
  ServerStageEntry,
  STAGE_ENTRY_FULL_SHAPE,
  STAGE_RUN_FULL_SHAPE,
  StageRun,
} from 'workflows/WorkflowConstants';

interface EnhancedProps {
  stageRuns: Array<StageRun>;
  stageRunsById: {[stageRunId: string]: StageRun};
  stageEntriesById: {[stageEntryId: string]: ServerStageEntry};
  stageRunIdByStageFlowNodeId: {[stageFlowNodeId: string]: string};
}

function mapStateToProps(
  state: BenchlingState,
  {workflowStageId}: BaseUpdateStageRunStatusModalProps
): EnhancedProps {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const workflowStage = workflowSelector.getWorkflowStage(workflowStageId)!;
  const stageEntries = workflowSelector.getStageEntries(workflowStage);
  const stageEntriesById: {[stageEntryId: string]: ServerStageEntry} = _.indexBy(stageEntries, 'id');
  const stageRuns = workflowSelector.getStageRunsForStageId(workflowStageId);
  const stageRunsById: {[stageRunId: string]: StageRun} = _.object(
    stageRuns.map((stageRun: StageRun) => [stageRun.id, stageRun])
  );
  const stageRunIdByStageFlowNodeId: {[stageFlowNodeId: string]: string} = _.object(
    stageRuns.map((stageRun: StageRun) => [stageRun.stageFlowNode.id, stageRun.id])
  );
  return {
    stageRuns,
    stageRunsById,
    stageEntriesById,
    stageRunIdByStageFlowNodeId,
  };
}
interface StageRunUpdateStatusSpecifier {
  stageRunId: string;
  status: STAGE_RUN_STATUS;
}

interface BaseUpdateStageRunStatusModalProps {
  workflowStageId: string;
  onRequestClose: () => void;
}

type UpdateStageRunStatusModalProps = BaseUpdateStageRunStatusModalProps & EnhancedProps;

interface State {
  selectedStageRunIds: Array<string>;
  statusByStageRunId: {[stageRunId: string]: STAGE_RUN_STATUS};
  statusChanged: boolean;
}

type Column = SortableTableColumn & {accessorKey: keyof RowData};
interface RowData {
  stageRunName: JSX.Element;
  status: JSX.Element;
}
interface Row {
  id: string;
  data: RowData;
  onClick: () => void;
  onDoubleClick: () => void;
  checked: boolean;
}

class UpdateStageRunStatusModal extends React.Component<UpdateStageRunStatusModalProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    workflowStageId: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,

    // Provided by connect.
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE),
    stageRunsById: PropTypes.objectOf(STAGE_RUN_FULL_SHAPE),
    stageEntriesById: PropTypes.objectOf(STAGE_ENTRY_FULL_SHAPE),
    stageRunIdByStageFlowNodeId: PropTypes.objectOf(PropTypes.string),
  };

  constructor(props: UpdateStageRunStatusModalProps) {
    super(props);
    this.state = {
      selectedStageRunIds: props.stageRuns
        .filter((stageRun) => this._isStageRunSelectedByDefault(stageRun))
        .map((stageRun) => stageRun.id),
      statusByStageRunId: {},
      statusChanged: false,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _isStageRunSelectedByDefault = (stageRun: StageRun) => {
    const isStageRunWritable = !this._isStageRunReviewed(stageRun.id);
    const isStageRunInitialized = stageRun.status === STAGE_RUN_STATUS.INITIALIZED;
    return isStageRunInitialized && isStageRunWritable;
  };

  _isStageRunReviewed(stageRunId: string): boolean {
    const stageRun = this.props.stageRunsById[stageRunId];
    // Look up full stage entry by summary ID.
    const stageEntry = this.props.stageEntriesById[stageRun.stageEntry.id];
    assertAndContinue(Boolean(stageEntry), `Stage entry ${stageRun.stageEntry.id} not found in store.`);
    return Boolean(stageEntry && stageEntry.review && stageEntry.review.status === ReviewConstants.ACCEPTED);
  }

  _getStageRunUpdateStatusSpecifiers(): Array<StageRunUpdateStatusSpecifier> {
    const patches = [];
    for (const stageRun of this.props.stageRuns) {
      const newStatus = this.state.statusByStageRunId[stageRun.id];
      if (newStatus && stageRun.status !== newStatus) {
        patches.push({stageRunId: stageRun.id, status: newStatus});
      }
    }
    return patches;
  }

  _areAnyStageRunsDiscarded(): boolean {
    return (
      null !=
      this._getStageRunUpdateStatusSpecifiers().find(({status}) => status === STAGE_RUN_STATUS.DISCARDED)
    );
  }

  _isStageRunSelected(stageRunId: string): boolean {
    return this.state.selectedStageRunIds.includes(stageRunId);
  }

  _getCurrentStatus(stageRun: StageRun): STAGE_RUN_STATUS {
    // If the status hasn't been set so far, return the current status
    return this.state.statusByStageRunId[stageRun.id] || stageRun.status;
  }

  _getParentsIdsInSameStage(stageRunId: string): Array<string> {
    return this.props.stageRunsById[stageRunId].stageFlowNode.parentIds
      .map((parentNodeId) => this.props.stageRunIdByStageFlowNodeId[parentNodeId])
      .filter((val: string) => _.identity(val));
  }

  _getIncompatibleSelection(): [string, string] | null {
    for (const stageRun of this.props.stageRuns) {
      const stageRunId = stageRun.id;
      if (!this._isStageRunSelected(stageRunId)) {
        continue;
      }
      // We'll search it's parent graph looking for other selected nodes
      const ancestorsIdsToCheck = this._getParentsIdsInSameStage(stageRunId);
      while (ancestorsIdsToCheck.length > 0) {
        // BFS with early return
        const ancestor = ancestorsIdsToCheck.pop()!;
        if (this._isStageRunSelected(ancestor)) {
          // Ding ding ding! We have a match
          return [ancestor, stageRunId];
        }
        ancestorsIdsToCheck.push(...this._getParentsIdsInSameStage(ancestor));
      }
    }
    return null;
  }

  _renderIncompatibleSelectionsError(): JSX.Element | null {
    const incompatibleSelections = this._getIncompatibleSelection();
    if (!incompatibleSelections) {
      return null;
    }
    const [firstName, secondName] = incompatibleSelections.map(
      (stageRunId) => this.props.stageRunsById[stageRunId].name
    );
    return (
      <div className="alert alert-danger u-small-spacing">
        Cannot update both {firstName} and {secondName} {'as '}
        {secondName} is downstream from {firstName}.
      </div>
    );
  }

  _getNumOfSelectedStageRuns(): number {
    return this.state.selectedStageRunIds.length;
  }

  _getNumOfStageRunsText(): string {
    const numOfSelectedStageRuns = this._getNumOfSelectedStageRuns();
    if (numOfSelectedStageRuns === 0) {
      const numOfStageRuns = this.props.stageRuns.length;
      return `${numOfStageRuns} stage ${pluralize(numOfStageRuns, 'run')}`;
    } else {
      return `${numOfSelectedStageRuns} stage ${pluralize(numOfSelectedStageRuns, 'run')} selected`;
    }
  }

  _handleChangeSelection(selectedStageRunIds: Array<string>): void {
    this.setState({selectedStageRunIds});
  }

  _handleSetStatus(status: STAGE_RUN_STATUS): void {
    const newStatusesByStageRunId = _.object(
      this.state.selectedStageRunIds.map((stageRunId) => [stageRunId, status])
    );
    this.setState({
      statusChanged: true,
      statusByStageRunId: {...this.state.statusByStageRunId, ...newStatusesByStageRunId},
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSubmit = async () => {
    const updateStatusSpecifiers = this._getStageRunUpdateStatusSpecifiers();
    if (updateStatusSpecifiers.length > 0) {
      await StageRunActions.bulkUpdateStageRunStatus({updateStatusSpecifiers});
    }
    this.props.onRequestClose();
  };

  _getButtons(): Array<ModalButton> {
    return [
      {
        text: 'Update',
        style: ButtonStyle.SECONDARY,
        disabled: !this.state.statusChanged || Boolean(this._getIncompatibleSelection()),
        onClick: this._handleSubmit,
        key: 'update',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _renderColumn = (column: Column, row: Row) => row.data[column.accessorKey];

  _fadeIfReviewed(stageRun: StageRun, text: string): JSX.Element {
    return <div className={classNames({'u-faded': this._isStageRunReviewed(stageRun.id)})}>{text}</div>;
  }

  render(): JSX.Element {
    const statusOptions = [
      STAGE_RUN_STATUS.INITIALIZED,
      STAGE_RUN_STATUS.COMPLETED,
      STAGE_RUN_STATUS.DISCARDED,
    ].map((status, idx) => ({
      name: STATUS_FRIENDLY_NAME[status],
      key: status,
      sortOrder: idx,
      submenu: null,
      handleOnClick: () => {
        this._handleSetStatus(status);
      },
    }));

    const columns: Array<SortableTableColumn> = [
      SortableTable.defaultColumn('Stage run name', 'stageRunName', {
        render: this._renderColumn,
        sortable: false,
      }),
      SortableTable.defaultColumn('Status', 'status', {render: this._renderColumn, sortable: false}),
    ];

    const rows: Array<Row> = this.props.stageRuns.map((stageRun) => ({
      data: {
        stageRunName: this._fadeIfReviewed(stageRun, stageRun.name),
        status: this._fadeIfReviewed(stageRun, STATUS_FRIENDLY_NAME[this._getCurrentStatus(stageRun)]),
      },
      id: stageRun.id,
      onClick: noop,
      onDoubleClick: noop,
      checked: this._isStageRunSelected(stageRun.id),
    }));
    return (
      <Modal
        title="Update status for stage runs"
        onRequestClose={this.props.onRequestClose}
        buttons={this._getButtons()}
      >
        <div>
          <Row className="u-vertical-center-items u-small-spacing">
            <Col md={4} />
            <Col md={4} className="u-text-align-right">
              {this._getNumOfStageRunsText()}
            </Col>
            <Col md={4}>
              <LegacySelectDropdown
                displayName="Set status"
                items={[statusOptions]}
                disabled={this._getNumOfSelectedStageRuns() === 0}
              />
            </Col>
          </Row>
          <Row className="updateStageRunStatusModal-bodyContainer">
            <Col md={12}>
              <CheckableSortableTable
                columns={columns}
                rows={rows}
                onChangeChecks={(stageRunIds: Array<string>) => {
                  this._handleChangeSelection(stageRunIds);
                }}
              />
            </Col>
          </Row>
          {this._areAnyStageRunsDiscarded() && (
            <div className="alert alert-warning u-small-spacing">
              Discarding a stage run will also delete all downstream stage runs. This action is not
              reversible.
            </div>
          )}
          {this._renderIncompatibleSelectionsError()}
        </div>
      </Modal>
    );
  }
}

export default connect(mapStateToProps)(UpdateStageRunStatusModal);
